import React, { useRef, useState } from 'react';

import { getFileVersionDisplayName } from '@aline/common';
import { link } from '@aline/routes';
import { Card, Divider, Grid, IconButton, InputBase, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { AlineTooltip, Spacer, UsersAvatarGroup } from 'src/components';
import { ArrowRightIcon } from 'src/components/icons/ArrowRightIcon';
import { VertDotsIcon } from 'src/components/icons/VertDotsIcon';
import { Apollo } from 'src/gql/generated';
import { getOnEnterHandler, useVersionHistoryParams } from 'src/helpers';
import { useIsAuthorizedToEditStorable } from 'src/hooks';
import type { ReactSetStateAction } from 'src/types';
import { VersionCardMenu } from 'src/version-history/VersionCardMenu';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';

import type { MonthDay } from './VersionCardList';

const useStyles = makeStyles<StyleProps>()((theme, { isActiveVersion }) => ({
  authorContainer: {
    marginTop: theme.spacing(1),
  },
  card: {
    '&:not(:last-child)': {
      marginBottom: theme.spacing(2),
    },
    border: isActiveVersion ? `1px solid ${theme.palette.primary.main}` : undefined,
    cursor: 'pointer',
  },
  cardContent: {
    padding: theme.spacing(0.5, 1, 1, 1),
  },
  footer: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    padding: theme.spacing(1),
  },
  latestVersion: {
    fontStyle: 'italic',
  },
  moreVersionsText: {
    fontSize: 12,
    fontWeight: 600,
  },
  provenance: {
    display: 'block',
    fontSize: 12,
  },
  userName: {
    marginLeft: theme.spacing(1),
  },
  versionName: {
    '&:focus-within': {
      border: `1px solid ${theme.appColors.rule}`,
    },
    '&:hover': {
      border: `1px solid ${theme.appColors.rule}`,
    },
    border: `1px solid transparent`,
    borderRadius: 3,
    fontSize: 12,
    fontWeight: 600,
    padding: theme.spacing(0.5, 0.5, 0.5, 0),
  },
  vertDotsButton: {
    alignSelf: 'self-start',
  },
  vertDotsColor: {
    color: theme.appColors.iconsType,
  },
}));

interface StyleProps {
  isActiveVersion: boolean;
}

interface VersionCardDocumentVersion extends Pick<
  Apollo.DocumentVersion,
  'createdAt' | 'documentJSON' | 'id' | 'name' | '__typename'
> {
  authors: Pick<Apollo.User, 'avatarColor' | 'displayName' | '__typename'>[];
  taskUpload?: Apollo.VersionTaskUploadFragment | null;
}
interface VersionCardStaticFileVersion extends Pick<
  Apollo.StaticFileVersion,
  'createdAt' | 'id' | 'name' | '__typename'
> {
  authors: Pick<Apollo.User, 'avatarColor' | 'displayName' | '__typename'>[];
  fileLocation: Pick<Apollo.DocFile, 'id' | 'filestackUrl' | 'mimetype' | '__typename'>;
  taskUpload?: Apollo.VersionTaskUploadFragment | null;
}
export type VersionCardVersion = VersionCardDocumentVersion | VersionCardStaticFileVersion;

interface VersionCardProps {
  day: string;
  isLatestVersion: boolean;
  month: string;
  setMonthDay: ReactSetStateAction<MonthDay | null>;
  totalNumVersions: number;
  versionsGroupedByDay: VersionCardVersion[];
}

export function VersionCard({
  day,
  isLatestVersion,
  month,
  setMonthDay,
  totalNumVersions,
  versionsGroupedByDay,
}: VersionCardProps): JSX.Element {
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const [firstVersion] = versionsGroupedByDay;
  const [open, setOpen] = useState<boolean>(false);
  const [raised, setRaised] = useState<boolean>(false);
  const { documentId, versionId } = useVersionHistoryParams();
  const { canEdit } = useIsAuthorizedToEditStorable(documentId);
  const [updateFileVersion] = Apollo.useUpdateFileVersionMutation();
  const debouncedUpdateFileVersion = useDebouncedCallback(
    async (fileVersionId: UUID, name: string) =>
      await updateFileVersion({ variables: { input: { fileVersionId, props: { name } } } }),
    1000
  );
  const [inputNode, setInputNode] = useState<HTMLInputElement | null>(null);
  const { classes } = useStyles({
    isActiveVersion: versionId
      ? versionsGroupedByDay.some(v => v.id === versionId)
      : isLatestVersion,
  });
  const [name, setName] = useState<string>(getFileVersionDisplayName(firstVersion));
  const showSubText = isLatestVersion || firstVersion.name;
  const subText = isLatestVersion
    ? 'Current version'
    : format(parseISO(firstVersion.createdAt), 'MMMM d - h:mm aaa');

  return (
    <>
      <Card
        ref={cardRef}
        className={classes.card}
        raised={raised}
        onClick={(): void => {
          if (versionId !== firstVersion.id)
            navigate(link.DocumentDetailVersionDetail({ documentId, versionId: firstVersion.id }));
        }}
        onMouseEnter={() => setRaised(true)}
        onMouseLeave={() => setRaised(false)}
      >
        <div className={classes.cardContent}>
          <Grid
            container
            sx={{
              alignItems: 'center',
            }}
            wrap="nowrap"
          >
            <InputBase
              multiline
              classes={{ root: classes.versionName }}
              disabled={!canEdit}
              inputRef={setInputNode}
              value={name}
              onChange={event => {
                setName(event.target.value);
                debouncedUpdateFileVersion(firstVersion.id, event.target.value);
              }}
              onClick={event => event.stopPropagation()}
              onFocus={event => event.target.select()}
              onKeyDown={getOnEnterHandler(() => inputNode?.blur())}
            />
            <Spacer />
            <AlineTooltip arrow placement="bottom" title="Options">
              <div className={classes.vertDotsButton}>
                <IconButton
                  classes={{ colorPrimary: classes.vertDotsColor }}
                  color="primary"
                  size="small"
                  onClick={(event: React.MouseEvent): void => {
                    event.stopPropagation();
                    setOpen(!open);
                  }}
                >
                  <VertDotsIcon />
                </IconButton>
              </div>
            </AlineTooltip>
          </Grid>
          {showSubText && (
            <Typography className={classes.latestVersion} color="textSecondary" variant="caption">
              {subText}
            </Typography>
          )}
          {firstVersion.taskUpload && (
            <Typography className={classes.provenance} color="textSecondary">
              {getTaskUploadProvenance(firstVersion.taskUpload)}
            </Typography>
          )}
          <Grid
            container
            className={classes.authorContainer}
            sx={{
              alignItems: 'center',
            }}
            wrap="nowrap"
          >
            <UsersAvatarGroup
              fallbackString="No author"
              tooltipPlacement="top"
              users={firstVersion.authors}
            />
          </Grid>
        </div>
        {versionsGroupedByDay.length > 1 && (
          <>
            <Divider variant="fullWidth" />
            <div className={classes.footer}>
              <Typography className={classes.moreVersionsText}>{`${
                versionsGroupedByDay.length - 1
              } more versions`}</Typography>
              <Spacer />
              <AlineTooltip arrow placement="bottom" title="View more">
                <IconButton
                  size="small"
                  onClick={event => {
                    event.stopPropagation();
                    setMonthDay({ day, month });
                  }}
                >
                  <ArrowRightIcon />
                </IconButton>
              </AlineTooltip>
            </div>
          </>
        )}
      </Card>
      <VersionCardMenu
        anchorEl={cardRef.current}
        canEdit={canEdit}
        disableDelete={totalNumVersions <= 1}
        isLatestVersion={isLatestVersion}
        open={open}
        setMonthDay={setMonthDay}
        setOpen={setOpen}
        version={firstVersion}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

function getTaskUploadProvenance(upload: Apollo.VersionTaskUploadFragment): string {
  const uploader = upload.uploadedBy ? ` by ${upload.uploadedBy.displayName}` : '';
  const title = upload.task.title || 'Untitled task';
  return `Uploaded for ${title}${uploader} · ${format(parseISO(upload.createdAt), 'MMM d')}`;
}
